import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import InputText from "commons/ui/input/InputText";
import IntegerInput from "commons/ui/input/IntegerInput";
import Select from "commons/ui/input/Select";
import SwitchInput from "commons/ui/input/SwitchInput";
import LineSeparator from "commons/ui/LineSeparator";
import Paper from "commons/ui/Paper";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import LanguageOrderableImages from "components/subComponents/LanguageOrderableImages";
import MetaFieldsForm from "components/subComponents/MetaFieldsForm";
import AttachmentPicker from "components/ui/AttachmentPicker";

import useProductForm from "hooks/form/useProductForm";

import { DesktopOrMobile, IndoorOrOutdoor } from "types/enums";

function ProductForm({ productId }: { productId?: string }) {
    const {
        setDesktopMobileImageThumbnail,
        setDesktopMobileImageCover,
        setSingleFile,
        productMeasureHook,
        productModuleHook,
        templateOptions,
        allCollection,
        allCategory,
        formHook,
    } = useProductForm({ productId });

    const { checkErrorVisibility, setValue, form } = formHook;

    return (
        <>
            <GridItem className="product-container">
                <Paper>
                    <GridColumn>
                        {form.title && (
                            <GridRow>
                                <GridItem className={"product-title"}>
                                    <h1>{form.title}</h1>
                                </GridItem>
                            </GridRow>
                        )}

                        <GridRow>
                            <GridItem className={"title-margin"}>
                                <h2>Dettagli Prodotto</h2>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <Select
                                    label={"Tipologia"}
                                    setValue={setValue}
                                    options={[
                                        { value: "", label: "" },
                                        { value: IndoorOrOutdoor.indoor, label: "Indoor" },
                                        {
                                            value: IndoorOrOutdoor.outdoor,
                                            label: "Outdoor",
                                        },
                                    ]}
                                    value={form.secondary_category}
                                    path={"secondary_category"}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <Select
                                    label={"Categoria"}
                                    setValue={setValue}
                                    options={[{ value: "", label: "" }, ...allCategory]}
                                    value={form.primary_category_id}
                                    path={"primary_category_id"}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <InputText
                                    label={"Nome"}
                                    setValue={setValue}
                                    value={form.name}
                                    path={"name"}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <Select
                                    label={"Collezione"}
                                    setValue={setValue}
                                    options={[{ value: "", label: "" }, ...allCollection]}
                                    value={form.collection_id}
                                    path={"collection_id"}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <IntegerInput
                                    label={"Anno"}
                                    setValue={setValue}
                                    value={form.year}
                                    path={"year"}
                                    placeholder={"AAAA"}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <InputText
                                    label={"Slug"}
                                    setValue={setValue}
                                    value={form.slug}
                                    path={"slug"}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <Select
                                    label={"Footer della pagina"}
                                    options={[{ value: "", label: "" }, ...templateOptions]}
                                    value={form.footer_template_id}
                                    path={"footer_template_id"}
                                    setValue={setValue}
                                    checkErrorVisibility={checkErrorVisibility}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-4 col-lg-24-8 col-xs-24-8"}>
                                <SwitchInput
                                    label={"Novità"}
                                    setValue={setValue}
                                    checked={form.is_new}
                                    path={"is_new"}
                                />
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <MetaFieldsForm
                        text={form.text}
                        setValue={setValue}
                        checkErrorVisibility={checkErrorVisibility}
                    />

                    <LineSeparator />

                    <GridColumn>
                        <GridRow>
                            <GridItem className={"col-xs-24-24 title-margin"}>
                                <h2>Visualizzazione</h2>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xl-24-4 col-lg-24-8 col-xs-24-8"}>
                                <SwitchInput
                                    label={"Visibile nell'area pubblica"}
                                    setValue={setValue}
                                    checked={form.front_visible}
                                    path={"front_visible"}
                                    alignedToForm
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-4 col-lg-24-8 col-xs-24-8"}>
                                <SwitchInput
                                    label={"Visibile nell'area riservata"}
                                    setValue={setValue}
                                    checked={form.reserved_area_visible}
                                    path={"reserved_area_visible"}
                                />
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <GridColumn>
                        <GridRow>
                            <div className={"col-xs-12-6"}>
                                <GridRow>
                                    <GridItem className={"col-xs-12-12 title-margin"}>
                                        <h2>Thumbnail</h2>
                                    </GridItem>
                                </GridRow>

                                <ExtensionsProporsionInfo
                                    desktopText={"600x338px (max 60KB)"}
                                    mobileText={"400x225px (max 50KB)"}
                                    proportion={"16:9"}
                                />

                                <GridRow>
                                    <GridItem className={"col-xl-12-6 col-lg-24-12 col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Desktop"}
                                            setFile={(file) =>
                                                setDesktopMobileImageThumbnail(
                                                    file,
                                                    DesktopOrMobile.desktop
                                                )
                                            }
                                            file={form.thumbnail.desktop}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("thumbnail.desktop")}
                                        />
                                    </GridItem>

                                    <GridItem className={"col-xl-12-6 col-lg-24-12 col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Mobile"}
                                            setFile={(file) =>
                                                setDesktopMobileImageThumbnail(
                                                    file,
                                                    DesktopOrMobile.mobile
                                                )
                                            }
                                            file={form.thumbnail.mobile}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("thumbnail.mobile")}
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>

                            <div className={"col-xs-12-6"}>
                                <GridRow>
                                    <GridItem className={"col-xs-12-12 title-margin"}>
                                        <h2>Copertina</h2>
                                    </GridItem>
                                </GridRow>

                                <ExtensionsProporsionInfo
                                    desktopText={"2000x 1125px (max 500KB)"}
                                    mobileText={"1200x 675px (max 250KB)"}
                                    proportion={"16:9"}
                                />

                                <GridRow>
                                    <GridItem className={"col-xl-12-6 col-lg-24-12 col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Desktop"}
                                            setFile={(file) =>
                                                setDesktopMobileImageCover(
                                                    file,
                                                    DesktopOrMobile.desktop
                                                )
                                            }
                                            file={form.cover.desktop}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("cover.desktop")}
                                        />
                                    </GridItem>

                                    <GridItem className={"col-xl-12-6 col-lg-24-12 col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Mobile"}
                                            setFile={(file) =>
                                                setDesktopMobileImageCover(
                                                    file,
                                                    DesktopOrMobile.mobile
                                                )
                                            }
                                            file={form.cover.mobile}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("cover.mobile")}
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>
                        </GridRow>
                    </GridColumn>
                </Paper>
            </GridItem>

            <GridItem className="product-container">
                <Paper>
                    <GridColumn>
                        <GridRow>
                            <GridItem className={"col-xs-24-24 title-margin"}>
                                <h2>File Caricati</h2>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"File 2D"}
                                    setFile={(file) => setSingleFile(file, "zip_2d")}
                                    file={form.zip_2d}
                                    accept={".png,.jpg,.jpeg,.pdf,.zip"}
                                    errors={checkErrorVisibility("zip_2d")}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"File 3D"}
                                    setFile={(file) => setSingleFile(file, "zip_3d")}
                                    file={form.zip_3d}
                                    accept={".png,.jpg,.jpeg,.pdf,.zip"}
                                    errors={checkErrorVisibility("zip_3d")}
                                />
                            </GridItem>

                            <GridItem className={"col-xl-24-6 col-lg-24-12 col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"File Dimensioni"}
                                    setFile={(file) => setSingleFile(file, "zip_dimension")}
                                    file={form.zip_dimension}
                                    accept={".png,.jpg,.jpeg,.pdf,.zip"}
                                    errors={checkErrorVisibility("zip_dimension")}
                                />
                            </GridItem>
                        </GridRow>
                    </GridColumn>
                </Paper>
            </GridItem>

            <GridItem className="product-container">
                <Paper>
                    <GridColumn className={"lang-orderable-img-title"}>
                        <GridRow>
                            <GridItem className={"col-xs-24-24 title-margin"}>
                                <h2>Moduli</h2>
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <GridItem className={"col-xs-12-12"}>
                        <LanguageOrderableImages languageOrderableImagesHook={productModuleHook} />
                    </GridItem>
                </Paper>
            </GridItem>

            <GridItem className="product-container">
                <Paper>
                    <GridColumn className={"lang-orderable-img-title"}>
                        <GridRow>
                            <GridItem className={"col-xs-24-24 title-margin"}>
                                <h2>Misure</h2>
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <GridItem className={"col-xs-12-12"}>
                        <LanguageOrderableImages languageOrderableImagesHook={productMeasureHook} />
                    </GridItem>
                </Paper>
            </GridItem>
        </>
    );
}

export default ProductForm;
